import { useId, useMemo, useRef, useState, type FormEvent } from 'react';
import { FormErrorSummary } from '@/components/catalog/FormShell';
import { isRealDate, without } from '@/components/catalog/form-utils';
import { ApiError, api } from '@/lib/api';
import { TARGET_PERIOD_KINDS, targetPeriodKindLabel } from '@/lib/targets';
import type {
  Cycle,
  TargetPeriodCreateInput,
  TargetPeriodKind,
  TargetPeriodRecord,
  TargetPeriodUpdateInput,
} from '@/lib/types';
import { Button, Checkbox, Field, Input, Modal, Select } from '@/components/ui';

/* -------------------------------------------------------------------------
 * Create or edit one target period — the window a sales target is set over.
 *
 * ## Why this exists at all
 *
 * `f_target_periods` shipped EMPTY and there was no way to create a row from
 * this app, so `UserTargetsModal` correctly said "No target periods exist yet"
 * and offered nothing. Every target names a period, so that one gap made the
 * whole targets feature unreachable on day one. This is the other half.
 *
 * ## The two cross-field rules, and why only ONE of them is a pair here
 *
 * ⚠️ **Every rule on this endpoint runs on the POST-MERGE state**, which is the
 * OPPOSITE of `{billDate, dueDate}` on an invoice and `{issueDate, validTill}`
 * on a quote. Those are checked only when the server sees both keys in one body,
 * so those forms re-send the unchanged half. This one must NOT reason that way,
 * and the difference splits the two rules apart:
 *
 * 1. **The DATES need no pairing.** `endDate` alone is compared against the
 *    STORED `startDate`, so an inverted window is refused however it is sent.
 *    `buildPayload` therefore sends only the date that changed. Re-sending the
 *    other half would be harmless but would also be a lie about why — and the
 *    next reader would carry it to an endpoint where it matters in reverse.
 * 2. ⚠️ **`kind` DRAGS `cycleId` with it, always.** Patching `kind` alone
 *    re-validates the STORED cycle: moving a `cycle` period to `month` while a
 *    cycle is stored is a 422 on `cycleId`, a field the user never touched, and
 *    the server refuses rather than auto-clearing (quietly discarding a recorded
 *    value is data loss nobody asked for). So the pair is emitted STRUCTURALLY
 *    whenever the kind moves — never as two independent diffs, which is exactly
 *    what a per-key loop produces. Same shape as `{metric, baseCurrency}` in
 *    `UserTargetsModal`.
 *
 * ## The four other things that bite
 *
 * - ⚠️ **`cycleId: ''` is a 422, NOT a clear.** Only `null` clears. That inverts
 *   what a cleared `<select>` hands you, so the empty string is mapped to `null`
 *   in `buildPayload` and can never reach the wire.
 * - ⚠️ **UNIQUE is `(kind, startDate, endDate)` — the NAME is not unique.** The
 *   409 names the kind and both dates and carries **no code**, deliberately: the
 *   sentence is the whole content, because the caller has to see which window
 *   collided. It is rendered verbatim, never matched on.
 * - **Dates are plain `YYYY-MM-DD` and calendar-checked** (`2026-02-30` is a
 *   422). Nothing here constructs a `Date` from one: a native date input already
 *   emits the wire format from local parts, and comparison is lexicographic,
 *   which is exact for a fixed-width zero-padded format.
 * - **Writes are `ADMIN_ROLES`.** The caller gates the control; this form still
 *   renders the 403, because a role can change under a live access token.
 * --------------------------------------------------------------------- */

export interface TargetPeriodFormModalProps {
  open: boolean;
  /** Omitted = create mode. Provided = edit mode. */
  period?: TargetPeriodRecord;
  /** The loaded cycle list, for the `kind: 'cycle'` picker. */
  cycles: Cycle[];
  cyclesLoading: boolean;
  /**
   * The cycle read FAILED, as against having returned nothing.
   *
   * Both arrive as an empty `cycles`, and on `kind: 'cycle'` the picker is
   * required — so without this it shows an empty required dropdown reading as
   * "no cycles exist yet" beside a Save button with no stated reason for being
   * disabled. Same prop, for the same reason, as `IntakeFormModal`'s.
   */
  cyclesFailed: boolean;
  onClose: () => void;
  onSaved: (period: TargetPeriodRecord) => void;
}

/** `f_target_periods.name` is VARCHAR(60); the schema trims and caps at 60. */
const NAME_MAX = 60;

interface FormValues {
  name: string;
  kind: TargetPeriodKind;
  startDate: string;
  endDate: string;
  /** The `<select>` value: a stringified cycle id, or '' for none chosen. */
  cycleId: string;
  isActive: boolean;
}

function toFormValues(period?: TargetPeriodRecord): FormValues {
  return {
    name: period?.name ?? '',
    kind: period?.kind ?? 'quarter',
    startDate: period?.startDate ?? '',
    endDate: period?.endDate ?? '',
    cycleId: period?.cycle ? String(period.cycle.id) : '',
    isActive: period?.isActive ?? true,
  };
}

/**
 * The `path` values a 422 may name that this form can actually MARK.
 *
 * Derived from the form's own value object rather than written out, so it cannot
 * drift from the controls. Every key here is a real write key AND has a control,
 * which is the test — a path naming a key the form does not render routes to an
 * off-screen input: it vanishes from the banner, inflates the "one field needs
 * attention" count, and highlights nothing.
 */
const KNOWN_WRITE_KEYS: readonly string[] = Object.keys(toFormValues());

export function TargetPeriodFormModal({
  open,
  period,
  cycles,
  cyclesLoading,
  cyclesFailed,
  onClose,
  onSaved,
}: TargetPeriodFormModalProps) {
  if (!open) return null;
  return (
    <TargetPeriodForm
      key={period?.id ?? 'new'}
      period={period}
      cycles={cycles}
      cyclesLoading={cyclesLoading}
      cyclesFailed={cyclesFailed}
      onClose={onClose}
      onSaved={onSaved}
    />
  );
}

function TargetPeriodForm({
  period,
  cycles,
  cyclesLoading,
  cyclesFailed,
  onClose,
  onSaved,
}: Omit<TargetPeriodFormModalProps, 'open'>) {
  const formId = useId();
  const nameRef = useRef<HTMLElement | null>(null);
  const isEdit = period !== undefined;

  const baseline = useMemo(() => toFormValues(period), [period]);
  const [values, setValues] = useState<FormValues>(baseline);
  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const errors = useMemo(
    () => ({ ...clientErrors, ...serverErrors }),
    [clientErrors, serverErrors],
  );

  const needsCycle = values.kind === 'cycle';

  function setField<K extends keyof FormValues>(key: K, next: FormValues[K]) {
    setValues((previous) => ({ ...previous, [key]: next }));
    setClientErrors((previous) => (key in previous ? without(previous, key) : previous));
    setServerErrors((previous) => (key in previous ? without(previous, key) : previous));
    /*
     * The date-order rule is reported against `endDate`, so moving the START
     * must clear it too — otherwise correcting the start date leaves a stale
     * message on a field that is now fine.
     */
    if (key === 'startDate') {
      setClientErrors((previous) => ('endDate' in previous ? without(previous, 'endDate') : previous));
      setServerErrors((previous) => ('endDate' in previous ? without(previous, 'endDate') : previous));
    }
    /*
     * Likewise the pairing rule: it is reported against `cycleId` but is caused
     * by `kind`, so changing the kind clears it. Without this, switching from
     * `cycle` to `month` leaves "must name the cycle it covers" sitting under a
     * control that is no longer rendered.
     */
    if (key === 'kind') {
      setClientErrors((previous) => ('cycleId' in previous ? without(previous, 'cycleId') : previous));
      setServerErrors((previous) => ('cycleId' in previous ? without(previous, 'cycleId') : previous));
    }
  }

  /**
   * The cycle id this form would SEND, as the wire wants it.
   *
   * ⚠️ Three values, not two. `''` is a **422** on this endpoint rather than a
   * clear — the opposite of what a cleared `<select>` hands you — so the empty
   * string is mapped here and can never reach the request. `null` clears,
   * `undefined` leaves the column alone.
   *
   * ⚠️ **Read through `values.kind`, not through the rendered control.** The
   * picker is HIDDEN on every kind but `cycle` and its state is deliberately left
   * alone while hidden, so that switching back restores the choice instead of
   * blanking it. That means `values.cycleId` can hold a real id on a `month`
   * period, and sending it would be a 422. The kind decides, always.
   */
  function cycleIdToSend(): number | null {
    if (values.kind !== 'cycle') return null;
    const trimmed = values.cycleId.trim();
    return trimmed === '' ? null : Number(trimmed);
  }

  const changedKeys = useMemo(() => {
    const keys = Object.keys(baseline) as (keyof FormValues)[];
    return keys.filter((key) => {
      const next = values[key];
      const previous = baseline[key];
      /*
       * Compared TRIMMED, because the payload is sent trimmed. Without this,
       * typing a trailing space marks the form dirty and then PATCHes a `name`
       * byte-identical to the stored one — a write that changes nothing, and a
       * Save button that lights up for a keystroke the server will ignore.
       */
      return typeof next === 'string' && typeof previous === 'string'
        ? next.trim() !== previous.trim()
        : next !== previous;
    });
  }, [values, baseline]);

  /**
   * The PATCH body: the diff, plus the one pair that cannot be diffed key by key.
   *
   * See the header. `kind` and `cycleId` are emitted TOGETHER whenever the kind
   * moves; the two dates are emitted independently because the server compares
   * each against the stored other.
   */
  function buildPatch(): TargetPeriodUpdateInput {
    const body: TargetPeriodUpdateInput = {};

    if (changedKeys.includes('name')) body.name = values.name.trim();
    if (changedKeys.includes('startDate')) body.startDate = values.startDate.trim();
    if (changedKeys.includes('endDate')) body.endDate = values.endDate.trim();
    if (changedKeys.includes('isActive')) body.isActive = values.isActive;

    if (changedKeys.includes('kind')) {
      body.kind = values.kind;
      // Structural, not conditional. Leaving it out here is the 422 this whole
      // comment block exists to prevent, and it names a field nobody touched.
      body.cycleId = cycleIdToSend();
    } else if (changedKeys.includes('cycleId')) {
      body.cycleId = cycleIdToSend();
    }

    return body;
  }

  function buildCreate(): TargetPeriodCreateInput {
    const cycleId = cycleIdToSend();
    return {
      name: values.name.trim(),
      kind: values.kind,
      startDate: values.startDate.trim(),
      endDate: values.endDate.trim(),
      // Omitted rather than sent as null on a non-cycle kind: null is accepted,
      // but the key means nothing there and the schema is `.strict()` about
      // shape, not about intent. Sending only what the record has is the house
      // rule, and it keeps the create body readable in the audit trail.
      ...(cycleId === null ? {} : { cycleId }),
      isActive: values.isActive,
    };
  }

  const patch = isEdit ? buildPatch() : null;
  // Derived from the payload itself, so "Save is enabled" and "there is
  // something to send" cannot disagree — an empty PATCH is a guaranteed 422.
  const isDirty = patch === null || Object.keys(patch).length > 0;

  function validate(): Record<string, string> {
    const found: Record<string, string> = {};

    const name = values.name.trim();
    if (name === '') found.name = 'A name is required.';
    else if (name.length > NAME_MAX) found.name = `Must be ${NAME_MAX} characters or fewer.`;

    const start = values.startDate.trim();
    const end = values.endDate.trim();
    for (const [key, value, label] of [
      ['startDate', start, 'Start date'],
      ['endDate', end, 'End date'],
    ] as const) {
      if (value === '') found[key] = `${label} is required.`;
      else if (!isRealDate(value)) found[key] = `${label} must be a real date in YYYY-MM-DD form.`;
    }

    /*
     * Lexicographic comparison, no `Date` anywhere. It is exact for a fixed-width
     * zero-padded `YYYY-MM-DD`, and constructing a Date is how an end-of-period
     * date loses a day west of Greenwich — which on a period BOUNDARY is the
     * difference between two quarters. Equal dates are allowed: a one-day window
     * is legitimate and the server permits it.
     */
    if (!found.startDate && !found.endDate && start > end) {
      found.endDate = 'The end date must be on or after the start date.';
    }

    if (needsCycle && values.cycleId.trim() === '') {
      found.cycleId = 'A cycle period must name the cycle it covers.';
    }

    return found;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    const found = validate();
    setClientErrors(found);
    setServerErrors({});
    setFormError(null);
    if (Object.keys(found).length > 0) return;

    if (patch !== null && Object.keys(patch).length === 0) {
      // Belt and braces: Save is disabled in this state, and an empty PATCH is a
      // guaranteed 422 that must never leave the client.
      setFormError('Nothing has changed yet.');
      return;
    }

    setSubmitting(true);
    try {
      const result =
        period && patch !== null
          ? await api.updateTargetPeriod(period.id, patch)
          : await api.createTargetPeriod(buildCreate());
      onSaved(result.data);
      onClose();
    } catch (caught) {
      handleError(caught);
      setSubmitting(false);
    }
  }

  function handleError(caught: unknown) {
    if (!(caught instanceof ApiError)) {
      setFormError('Could not reach the server. Check your connection and try again.');
      return;
    }

    if (caught.status === 403) {
      setServerErrors({});
      setFormError(
        'The server refused this. Target periods can only be changed by an administrator, and nothing was saved.',
      );
      return;
    }

    if (caught.status === 404) {
      setServerErrors({});
      setFormError(
        'This period no longer exists — somebody may have deleted it. Close and reopen the list.',
      );
      return;
    }

    if (caught.status === 409) {
      /*
       * ⚠️ Rendered VERBATIM and never matched on.
       *
       * The duplicate-window 409 deliberately carries no code, because its useful
       * half is the sentence — "A 'quarter' period already covers 2026-04-01 to
       * 2026-06-30" — and no code can carry which window collided. Composing our
       * own sentence here would also state the key wrongly: it is
       * `(kind, startDate, endDate)`, so a NAME somebody has never seen before is
       * not what clashed, and telling them to rename it sends them nowhere.
       */
      setServerErrors({});
      setFormError(caught.message);
      return;
    }

    /*
     * Everything else, routed against this form's OWN write-key list rather than
     * against the shape of the path. `unroutable` is the empty-path `.strict()`
     * and "at least one field" refusals plus any path naming a key with no
     * control here; rendering it at form level is the only way it is seen at all.
     */
    const { fields, unroutable } = caught.routeFieldErrors(KNOWN_WRITE_KEYS);
    setServerErrors(fields);
    if (unroutable.length > 0) setFormError(unroutable.join(' '));
    else if (Object.keys(fields).length === 0) setFormError(caught.message);
  }

  const fieldErrorCount = Object.keys(errors).length;
  const saveDisabled = isEdit
    ? !isDirty
    : values.name.trim() === '' || values.startDate === '' || values.endDate === '';

  /*
   * A cycle the period points at but the list does not carry — a cycle paged out
   * beyond the first 100. Injected so an existing period's picker does not read
   * as "none chosen", which the next save would turn into a real 422 on a value
   * the user never touched. Same rule as `withStoredOption()`.
   */
  const storedCycle = period?.cycle ?? null;
  const missingCycle =
    storedCycle !== null && !cycles.some((cycle) => cycle.id === storedCycle.id)
      ? storedCycle
      : null;

  /**
   * Why the cycle picker cannot be used, or `null` when it can.
   *
   * ⚠️ A bare `disabled` renders no explanation, which makes "we could not read
   * the cycles", "there are no cycles" and "this control is not for you" look
   * identical. Each gets its own sentence, and a failed read is never presented
   * as an empty list — with no list the only reachable outcome is clearing a
   * value the reader can see.
   */
  const cycleDisabledReason = cyclesFailed
    ? 'The cycle list could not be read, so a cycle cannot be chosen right now.'
    : !cyclesLoading && cycles.length === 0
      ? 'There are no cycles yet. Add one under Cycles first, then this kind can name it.'
      : null;

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      dismissOnBackdrop={false}
      initialFocusRef={nameRef}
      title={period ? `Edit ${period.name}` : 'New target period'}
      /*
       * ⚠️ The edit-only description is GONE (2026-09-05). Moving a boundary
       * still silently re-scopes every target already set against the period,
       * and nothing on this form says so any more.
       */
      footer={
        <>
          {isEdit && !isDirty && (
            <p className="self-center text-xs text-fg-muted sm:mr-auto">No changes yet.</p>
          )}
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            loading={submitting}
            disabled={saveDisabled}
          >
            {isEdit ? 'Save changes' : 'Create period'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} noValidate className="space-y-4">
        <FormErrorSummary formError={formError} fieldErrorCount={fieldErrorCount} />

        <div className="grid gap-3 sm:grid-cols-2">
          <div
            className="sm:col-span-2"
            ref={(node) => {
              nameRef.current = node?.querySelector('input') ?? null;
            }}
          >
            <Field label="Name" required error={errors.name}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  value={values.name}
                  maxLength={NAME_MAX}
                  invalid={Boolean(errors.name)}
                  aria-describedby={describedBy}
                  onChange={(event) => setField('name', event.target.value)}
                />
              )}
            </Field>
          </div>

          <Field label="Kind" required error={errors.kind}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={values.kind}
                invalid={Boolean(errors.kind)}
                onChange={(event) => setField('kind', event.target.value as TargetPeriodKind)}
              >
                {TARGET_PERIOD_KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {targetPeriodKindLabel(kind)}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          {/*
            ⚠️ HIDDEN on every other kind, not disabled — and the state behind it
            is deliberately LEFT ALONE. `cycleIdToSend()` reads `values.kind`
            rather than the control, so a stored id cannot leak onto a `month`
            period, and switching back to `cycle` restores what was chosen
            instead of blanking it. Same pattern as the currency box in
            `UserTargetsModal`.
          */}
          {needsCycle ? (
            /* `hint` carries the DISABLED REASON only now — the "first page
               only, a cycle may be missing" truncation half went in the
               2026-09-05 sweep. */
            <Field label="Cycle" required error={errors.cycleId} hint={cycleDisabledReason ?? undefined}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={values.cycleId}
                  disabled={cycleDisabledReason !== null || cyclesLoading}
                  invalid={Boolean(errors.cycleId)}
                  onChange={(event) => setField('cycleId', event.target.value)}
                >
                  <option value="">{cyclesLoading ? 'Loading cycles…' : 'Select a cycle…'}</option>
                  {missingCycle && (
                    <option value={String(missingCycle.id)}>
                      {missingCycle.cycleName} — not in the list
                    </option>
                  )}
                  {cycles.map((cycle) => (
                    <option key={cycle.id} value={String(cycle.id)}>
                      {cycle.cycleName}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          ) : null}

          <Field label="Start date" required error={errors.startDate}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                // A native date input emits YYYY-MM-DD built from local parts,
                // which is exactly the wire format — no Date, no drift.
                type="date"
                value={values.startDate}
                invalid={Boolean(errors.startDate)}
                aria-describedby={describedBy}
                onChange={(event) => setField('startDate', event.target.value)}
              />
            )}
          </Field>

          <Field label="End date" required error={errors.endDate}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                type="date"
                value={values.endDate}
                min={values.startDate || undefined}
                invalid={Boolean(errors.endDate)}
                aria-describedby={describedBy}
                onChange={(event) => setField('endDate', event.target.value)}
              />
            )}
          </Field>

          <div className="sm:col-span-2">
            {/*
              ⚠️ The description here said "Retired periods stay on existing
              targets; they stop being offered for new ones." — removed
              2026-09-05. A reader who now believes retiring hides history will
              reach for Delete, which is the irreversible option; the in-use
              delete refusal still catches that, but only after they try it.
            */}
            <Checkbox
              checked={values.isActive}
              onChange={(event) => setField('isActive', event.target.checked)}
              label="Offered when setting a target"
            />
          </div>
        </div>

      </form>
    </Modal>
  );
}
